import React, { useState, ChangeEvent, FormEvent } from 'react';
import * as m from 'framer-motion/m';
import toast from 'react-hot-toast';
import Spinner from './Spinner';
import { FiUser, FiPhone, FiMail, FiMessageSquare, FiFileText } from 'react-icons/fi';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';

const CongratulationTable = () => {
  const { t } = useTranslation('common');
  return (
    <div className='max-w-md mx-auto mt-8 p-6 bg-green-700 dark:bg-green-800 rounded-2xl shadow-xl text-white text-center'>
      <h2 className='text-3xl font-semibold mb-6'>
        {t('contactForm.success')}
      </h2>
      <p>{t('contactForm.successDesc')}</p>
      <p className='mt-2'>{t('contactForm.successThanks')}</p>
    </div>
  );
};

const ContactForm: React.FC = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const asPath = router.asPath ?? '';
  const isLithuanian = asPath.startsWith('/lt');
  const isUS = asPath.startsWith('/us');
  const successUrl = isLithuanian
    ? '/lt/kontaktai-sekmingai'
    : isUS
      ? '/us/contact-success'
      : '/contact-success';
  const toText = (v: string | string[]) => (Array.isArray(v) ? v.join(' ') : v);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(e.target.value);
      if (!e.target.value.trim()) {
        e.target.setCustomValidity('Required field');
      } else {
        e.target.setCustomValidity('');
      }
    };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const phoneRegex = /^[+]?[0-9]*$/;
    if (!phoneRegex.test(phoneNumber)) {
      toast.error(toText(t('contactForm.invalidPhone')), {
        duration: 3000,
        position: 'top-center',
      });
      setLoading(false);
      return;
    }

    const formData = { name, phoneNumber, subject, message, clientEmail };

    try {
      const response = await fetch('/api/submitForm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        // ✅ Clear fields
        setName('');
        setPhoneNumber('');
        setSubject('');
        setMessage('');
        setClientEmail('');

        toast.success(toText(t('contactForm.success')), {
          duration: 1500,
          position: 'top-center',
        });

        // ✅ Redirect to success page for Google Ads tracking (same locale)
        setTimeout(() => {
          window.location.href = successUrl;
        }, 1500);
      } else {
        toast.error(toText(t('contactForm.fail')));
      }
    } catch (error) {
      toast.error(toText(t('contactForm.error')));
    } finally {
      setLoading(false);
    }
  };

  if (formSubmitted) return <CongratulationTable />;

  return (
    <m.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      id='contact-form'
      className='relative w-full max-w-2xl mx-auto glass-bg dark:glass-bg rounded-3xl border border-white/20 dark:border-transparent p-6 sm:p-8 shadow-xl'
    >
      <div className='mb-6'>
        <h2 className='text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2'>
          {toText(t('contactForm.getInTouch') as string | string[])}
        </h2>
        <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
          {toText(t('contactForm.haveQuestion') as string | string[])}
        </p>
      </div>

      {loading && (
        <div className='absolute inset-0 z-50 flex items-center justify-center bg-black/90 rounded-xl'>
          <Spinner />
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div className='relative'>
            <FiUser className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5' />
            <input
              type='text'
              placeholder={toText(t('contactForm.name') as string | string[])}
              value={name}
              onChange={handleChange(setName)}
              required
              className='w-full pl-10 pr-4 py-2 rounded-xl text-sm bg-white/80 dark:bg-black/40 text-gray-900 dark:text-gray-100 placeholder-gray-400 border border-gray-200 dark:border-gray-700/80 focus:outline-none focus:border-primary'
            />
          </div>

          <div className='relative'>
            <FiPhone className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5' />
            <input
              type='text'
              placeholder={toText(t('contactForm.phone') as string | string[])}
              value={phoneNumber}
              onChange={handleChange(setPhoneNumber)}
              required
              className='w-full pl-10 pr-4 py-2 rounded-xl text-sm bg-white/80 dark:bg-black/40 text-gray-900 dark:text-gray-100 placeholder-gray-400 border border-gray-200 dark:border-gray-700/80 focus:outline-none focus:border-primary'
            />
          </div>

          <div className='relative'>
            <FiFileText className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5' />
            <input
              type='text'
              placeholder={toText(t('contactForm.subject') as string | string[])}
              value={subject}
              onChange={handleChange(setSubject)}
              required
              className='w-full pl-10 pr-4 py-2 rounded-xl text-sm bg-white/80 dark:bg-black/40 text-gray-900 dark:text-gray-100 placeholder-gray-400 border border-gray-200 dark:border-gray-700/80 focus:outline-none focus:border-primary'
            />
          </div>

          <div className='relative'>
            <FiMail className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5' />
            <input
              type='email'
              placeholder={toText(t('contactForm.email') as string | string[])}
              value={clientEmail}
              onChange={handleChange(setClientEmail)}
              required
              className='w-full pl-10 pr-4 py-2 rounded-xl text-sm bg-white/80 dark:bg-black/40 text-gray-900 dark:text-gray-100 placeholder-gray-400 border border-gray-200 dark:border-gray-700/80 focus:outline-none focus:border-primary'
            />
          </div>
        </div>

        <div className='relative'>
          <FiMessageSquare className='absolute left-3 top-3 text-gray-400 dark:text-gray-500 w-5 h-5' />
          <textarea
            placeholder={toText(t('contactForm.message') as string | string[])}
            value={message}
            onChange={handleChange(setMessage)}
            required
            rows={4}
            className='w-full pl-10 pr-4 py-2 rounded-xl text-sm bg-white/80 dark:bg-black/40 text-gray-900 dark:text-gray-100 placeholder-gray-400 border border-gray-200 dark:border-gray-700/80 focus:outline-none focus:border-primary resize-none'
          />
        </div>

        <div className='pt-2'>
          <button
            type='submit'
            disabled={loading}
            className='w-full py-3 px-4 rounded-xl bg-primary hover:bg-primaryDark text-white text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {toText(t('contactForm.send') as string | string[])}
          </button>
          <p className='text-xs text-gray-500 dark:text-gray-400 text-center mt-3'>
            {toText(t('contactForm.allFields') as string | string[])}
          </p>
        </div>
      </form>
    </m.div>
  );
};

export default ContactForm;
